import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { request } from '../lib/api';
import { authenticate, authError, needsKey, needsTotp, type CustomerLoginResult } from '../lib/authSecurity';
import { TotpLoginPrompt } from '../components/TotpLoginPrompt';
import { KeyLoginPrompt } from '../components/KeyLoginPrompt';
import { GoogleSignIn } from '../components/GoogleSignIn';
import { consumeActivationReturn } from '../lib/activationReturn';
import { toSession } from '../lib/userSession';
import { useSession } from '../session';
import './customer-public-entry.css';

type RequestSessionResponse = {
  ok: true;
  status: 'accepted';
  email: string;
  expiresInMinutes: number;
};

type FlowStep = 'email' | 'link-sent' | 'verifying' | 'key' | 'totp';

function maskEmail(email: string) {
  const [user, domain] = email.split('@');
  if (!domain) return email;
  if (user.length <= 2) {
    return `${user[0] ?? ''}***@${domain}`;
  }
  return `${user.slice(0, 2)}***@${domain}`;
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

type LoginPageProps = {
  variant?: 'login' | 'create-account';
};

function LoginPage({ variant: variantProp }: LoginPageProps = {}) {
  const location = useLocation();
  const pathVariant = location.pathname === '/create-account' ? 'create-account' : 'login';
  const variant = variantProp ?? pathVariant;
  const isCreateFlow = variant === 'create-account';
  const navigate = useNavigate();
  const { session, setSession } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenParam = useMemo(() => searchParams.get('token'), [searchParams]);
  const confirmationParam = useMemo(() => searchParams.get('confirmation'), [searchParams]);
  const processedTokenRef = useRef<string | null>(null);

  const [step, setStep] = useState<FlowStep>('email');
  const [visualAvailable, setVisualAvailable] = useState(true);
  const [keyChallenge, setKeyChallenge] = useState<{ magicToken: string; hardwareRequired: boolean } | null>(null);
  const [totpToken, setTotpToken] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const isVerifying = step === 'verifying';
  const acceptLogin = useCallback((response: CustomerLoginResult) => {
    if (needsKey(response)) {
      setKeyChallenge(response);
      setStep('key');
      setInfoMessage(null);
      return;
    }
    if (needsTotp(response)) { setTotpToken(response.magicToken); setStep('totp'); setInfoMessage(null); return; }
    setKeyChallenge(null); setTotpToken(null);
    setSession(toSession(response));
  }, [setSession]);
  const passkeyLogin = async () => {
    const typedEmail = email.trim().toLowerCase();
    if (!typedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typedEmail)) { setError('Enter your account email before using a key or passkey.'); return; }
    setSubmitting(true); setError(null);
    try { acceptLogin(await authenticate<CustomerLoginResult>(false, { email: typedEmail, rememberMe })); }
    catch (err) { setError(authError(err)); }
    finally { setSubmitting(false); }
  };


  useEffect(() => {
    if (!confirmationParam) {
      return;
    }

    const confirmationKey = `confirmation:${confirmationParam}`;
    if (processedTokenRef.current === confirmationKey) {
      return;
    }

    processedTokenRef.current = confirmationKey;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('confirmation');

    setStep('verifying');
    setError(null);
    setInfoMessage('Confirming your email and creating your account...');
    setSubmitting(true);

    request<{ magicToken: string }>('/auth/verify-registration', {
      method: 'POST',
      body: JSON.stringify({ token: confirmationParam })
    })
      .then(({ magicToken }) => request<CustomerLoginResult>('/auth/verify-magic', {
        method: 'POST',
        body: JSON.stringify({ token: magicToken })
      }))
      .then((response) => {
        acceptLogin(response);
      })
      .catch((err: any) => {
        const message = err?.data?.message || 'Account confirmation link is invalid or expired. Request a new one.';
        setError(message);
        setStep('email');
      })
      .finally(() => {
        setSubmitting(false);
        setInfoMessage(null);
        setSearchParams(nextParams, { replace: true });
        processedTokenRef.current = null;
      });
  }, [acceptLogin, confirmationParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (!session) {
      return;
    }

    if (location.pathname === '/login' || location.pathname === '/create-account') {
      navigate(consumeActivationReturn() ?? '/dashboard', { replace: true });
    }
  }, [location.pathname, navigate, session]);

  useEffect(() => {
    if (!tokenParam) {
      return;
    }

    if (processedTokenRef.current === tokenParam) {
      return;
    }

    processedTokenRef.current = tokenParam;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('token');

    setStep('verifying');
    setError(null);
    setInfoMessage('Verifying magic link...');
    setSubmitting(true);

    request<CustomerLoginResult>('/auth/verify-magic', {
      method: 'POST',
      body: JSON.stringify({ token: tokenParam })
    })
      .then((response) => {
        acceptLogin(response);
      })
      .catch((err: any) => {
        const message = err?.data?.message || 'Magic link is invalid or expired. Request a new one.';
        setError(message);
        setStep('email');
      })
      .finally(() => {
        setSubmitting(false);
        setInfoMessage(null);
        setSearchParams(nextParams, { replace: true });
        processedTokenRef.current = null;
        setStep((prev) => {
          if (prev === 'verifying' && (location.pathname === '/login' || location.pathname === '/create-account')) {
            return 'email';
          }
          return prev;
        });
      });
  }, [acceptLogin, location.pathname, navigate, searchParams, setSearchParams, tokenParam]);

  const resetFlow = () => {
    setStep('email');
    setKeyChallenge(null); setTotpToken(null);
    setInfoMessage(null);
    setError(null);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const submitRequest = async (normalizedEmail: string) => {
    return request<RequestSessionResponse>('/auth/request-session', {
      method: 'POST',
      body: JSON.stringify({
        email: normalizedEmail,
        createIfMissing: isCreateFlow,
        rememberMe
      })
    });
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setError('Enter the email you used at checkout.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setInfoMessage(null);

    try {
      const response = await submitRequest(normalizedEmail);

      setEmail(normalizedEmail);
      setInfoMessage(
        `If ${maskEmail(response.email)} is eligible, an email is on its way. The one-time link expires in ${response.expiresInMinutes} minutes.`
      );
      setStep('link-sent');
    } catch (err: any) {
      const message = err?.data?.message || 'Sign-in failed. Please try again in a moment.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendLink = async () => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await submitRequest(normalizedEmail);
      setInfoMessage(
        `If ${maskEmail(response.email)} is eligible, another email is on its way. The one-time link expires in ${response.expiresInMinutes} minutes.`
      );
    } catch (err: any) {
      const message = err?.data?.message || 'Unable to resend the link. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`auth-shell ${isCreateFlow ? 'auth-shell--create' : ''}`}>
      <div className="auth-shell__layout">
      <div className="card-panel auth-shell__card">
        <h1>{isCreateFlow ? 'Create your InkTap access' : 'Welcome back to InkTap'}</h1>
        {isCreateFlow ? (
          <p className="helper-text">
            Use your best email — we’ll send a confirmation link first. Your Free account and card workspace are only
            created after you confirm that email. Ordering a card later upgrades you automatically.
          </p>
        ) : null}
        {infoMessage ? <p className="helper-text">{infoMessage}</p> : null}
        {error ? <div className="alert" role="alert">{error}</div> : null}

        {step === 'email' && (
          <form className="form-stack" onSubmit={handleEmailSubmit}>
            <p className="helper-text">
              {isCreateFlow
                ? 'Enter the email you want to use for InkTap. We\'ll send a one-time link to finish setup - no password required.'
                : 'Enter your account email. Use your registered key or passkey for direct sign-in without a password or emailed token. Email links and Google still require your enrolled credential.'}
            </p>
            <label>
              Email
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                required
                autoComplete="email"
                value={email}
                onChange={handleEmailChange}
                disabled={isSubmitting || isVerifying}
              />
            </label>
            <label className="auth-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                disabled={isSubmitting || isVerifying}
              />
              <span>
                <strong>Keep me signed in for 30 days</strong>
                <small>Leave this off on a shared device. Otherwise, this session lasts 12 hours.</small>
              </span>
            </label>
            <button className="button-primary" type="submit" disabled={isSubmitting || isVerifying}>
              {isSubmitting ? 'Sending...' : 'Email me a link'}
            </button>
          </form>
        )}

        {step === 'email' ? <div className="form-stack" style={{ marginTop: '1rem' }}>
          <button className="button-secondary" type="button" disabled={isSubmitting} onClick={passkeyLogin}>{isSubmitting ? 'Waiting...' : 'Sign in with email and key or passkey'}</button>
          <GoogleSignIn disabled={isSubmitting} rememberMe={rememberMe} createIfMissing={isCreateFlow} onResult={result => {
            if ('status' in result && result.status === 'link_required') { setError(result.message ?? 'Sign in with your existing method, then link Google in Account settings.'); return; }
            if ('status' in result && result.status === 'email_confirmation_required') { setEmail(result.email ?? email); setInfoMessage('Check your email to confirm your new InkTap account.'); setStep('link-sent'); return; }
            if ('sessionExpiresAt' in result || ('status' in result && (result.status === 'key_required' || result.status === 'totp_required'))) acceptLogin(result);
          }} />
          <p className="helper-text">After enrollment, a registered key or passkey is required at sign-in. Hardware-key accounts must use a registered hardware key, including after email or Google.</p>
        </div> : null}
        {step === 'totp' && totpToken ? <TotpLoginPrompt magicToken={totpToken} onSuccess={response => acceptLogin(response)} /> : null}
        {step === 'key' && keyChallenge ? <KeyLoginPrompt magicToken={keyChallenge.magicToken} hardwareRequired={keyChallenge.hardwareRequired} onSuccess={response => acceptLogin(response)} /> : null}

        {step === 'link-sent' && (
          <div className="form-stack" style={{ gap: '1rem' }}>
            <p className="helper-text">
              Check <strong>{maskEmail(email)}</strong>.{' '}
              {isCreateFlow
                ? 'Existing accounts receive a sign-in link; new accounts receive a confirmation link first.'
                : 'If an eligible account exists, it receives a one-time sign-in link.'}{' '}
              For privacy, this screen does not confirm whether an account exists.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button className="button-primary" type="button" onClick={handleResendLink} disabled={isSubmitting || isVerifying}>
                {isSubmitting ? 'Resending...' : 'Resend link'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEmail('');
                  resetFlow();
                }}
                className="auth-shell__alternate"
              >
                Use a different email
              </button>
            </div>
          </div>
        )}

        {step === 'verifying' && (
          <div className="helper-text">Hold on tight - verifying your magic link...</div>
        )}

        <p className="helper-text" style={{ marginTop: '1.5rem' }}>
          {isCreateFlow ? (
            <>
              Already on InkTap?{' '}
              <Link className="link-button" to="/login">
                Log in instead
              </Link>
              .
            </>
          ) : (
            <>
              Need an account?{' '}
              <Link className="link-button" to="/create-account">
                Create one now
              </Link>
              .
            </>
          )}
        </p>
        <p className="helper-text">
          Want to design your tap card?{' '}
          <Link className="link-button" to="/card-builder">
            Launch the card designer
          </Link>
          .
        </p>
      </div>
      {visualAvailable ? (
        <aside className="auth-shell__visual" aria-label="An InkTap introduction">
          <img
            src="/assets/introductions-hero.webp"
            alt="Example of a personalized business card being shared"
            width="1400"
            height="809"
            loading="lazy"
            decoding="async"
            onError={() => setVisualAvailable(false)}
          />
          <div className="auth-shell__visual-copy">
            <h2>A better way to introduce yourself.</h2>
            <p>A card and Tap Page made to share.</p>
          </div>
        </aside>
      ) : null}
      </div>
    </div>
  );
}

export default LoginPage;
